/**
 * ExpensesPage — admin-only broad expense tracking. Separate from the OCR
 * invoices flow (which stays construction-focused). Backed by /api/expenses.
 *
 * V1 scope:
 *   - CRUD with soft archive.
 *   - Filters: quarter, category, villa, status, show archived.
 *   - KPIs: total, paid, pending, reimbursed.
 *   - Multiple attachments per expense (files_service scope=expense_attachment).
 *
 * Out of scope: PDF reports, Excel export, currency conversion, approval flow.
 */
import React, { useEffect, useMemo, useState } from "react";
import AdminShell from "@/pages/admin/AdminShell";
import { StatCard, Section } from "@/pages/Shell";
import { api, formatError } from "@/lib/api";
import {
  Receipt, Plus, Paperclip, Archive, X, ArrowClockwise,
  ChartBar, DownloadSimple,
} from "@phosphor-icons/react";

const ACCENT = "#1C1C1E";

const EXPENSE_CATEGORIES = [
  { value: "construction", label: "Construcción" },
  { value: "materials",    label: "Materiales" },
  { value: "labor",        label: "Mano de obra" },
  { value: "legal",        label: "Legal / permisos" },
  { value: "marketing",    label: "Marketing / ventas" },
  { value: "operations",   label: "Operaciones" },
  { value: "taxes",        label: "Impuestos / tasas" },
  { value: "travel",       label: "Viajes / transporte" },
  { value: "software",     label: "Software / administración" },
  { value: "other",        label: "Otros" },
];
const CATEGORY_LABEL = Object.fromEntries(EXPENSE_CATEGORIES.map((c) => [c.value, c.label]));

const EXPENSE_STATUSES = [
  { value: "pending",    label: "Pendiente" },
  { value: "paid",       label: "Pagado" },
  { value: "reimbursed", label: "Reembolsado" },
];
const STATUS_LABEL = Object.fromEntries(EXPENSE_STATUSES.map((s) => [s.value, s.label]));
const STATUS_TONE = {
  pending:    "bg-amber-50 text-amber-800 border-amber-200",
  paid:       "bg-emerald-50 text-emerald-700 border-emerald-200",
  reimbursed: "bg-sky-50 text-sky-700 border-sky-200",
};

const CURRENCIES = ["EUR", "IDR", "USD"];

// Build the list of quarter options visible in the picker. We render the four
// quarters of the current year plus the four of the previous one; enough for
// day-to-day admin use without an infinite dropdown.
function currentQuarter() {
  const now = new Date();
  const q = Math.floor(now.getMonth() / 3) + 1;
  return `Q${q}-${now.getFullYear()}`;
}
function buildQuarterOptions() {
  const now = new Date();
  const y = now.getFullYear();
  const out = [];
  for (const yr of [y, y - 1]) {
    for (const q of [1, 2, 3, 4]) out.push(`Q${q}-${yr}`);
  }
  return out;
}

function fmtEur(n, cur = "EUR") {
  const v = Math.round(Number(n) || 0);
  const prefix = cur === "EUR" ? "€" : cur === "USD" ? "$" : "";
  const suffix = cur === "IDR" ? " IDR" : "";
  return `${prefix}${v.toLocaleString()}${suffix}`;
}

// Convert Q3-2026 into a "1 jul – 30 sep 2026" style label. Falls back to the
// quarter string when the backend didn't send a date_range (no quarter filter).
function formatDateRange(range) {
  if (!range?.start || !range?.end) return "";
  const months = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
  const parse = (iso) => {
    const [y, m, d] = iso.split("-").map(Number);
    return { y, m, d };
  };
  const s = parse(range.start);
  const e = parse(range.end);
  return `${s.d} ${months[s.m - 1]} – ${e.d} ${months[e.m - 1]} ${e.y}`;
}

// Escape a value for CSV: wrap in double quotes and double any inner ones.
// Numbers are rendered without thousand separators so Excel keeps them numeric.
function csvEscape(v) {
  if (v == null) return "";
  const s = String(v);
  if (/[",\n;]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function villaLabelForExport(exp, villaById) {
  const scope = exp?.villa_scope;
  if (scope === "project") return "Proyecto general";
  if (scope === "villa") return villaById[exp.villa_id]?.name || exp.villa_id || "";
  if (scope === "multi") {
    return (exp.villa_ids || [])
      .map((id) => villaById[id]?.name || id)
      .join(" | ");
  }
  return "";
}

function exportExpensesCSV(expenses, villaById, quarter) {
  const header = [
    "Fecha", "Proveedor", "Categoría", "Scope villa", "Villa/s",
    "Importe", "Moneda", "Estado", "Nota", "Tiene adjuntos", "ID gasto",
  ];
  const scopeLabel = { project: "Proyecto", villa: "Villa", multi: "Varias" };
  const rows = expenses.map((e) => [
    e.date || "",
    e.provider || "",
    CATEGORY_LABEL[e.category] || e.category || "",
    scopeLabel[e.villa_scope] || e.villa_scope || "",
    villaLabelForExport(e, villaById),
    e.amount != null ? String(e.amount) : "",
    e.currency || "",
    STATUS_LABEL[e.status] || e.status || "",
    e.note || "",
    (Number(e.attachments_count) || 0) > 0 ? "Sí" : "No",
    e.expense_id || "",
  ]);
  const csv = [header, ...rows].map((r) => r.map(csvEscape).join(",")).join("\n");
  // BOM so Excel opens UTF-8 accents correctly on Windows/macOS.
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `dolce-villas-gastos-${quarter || "todos"}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function villaScopeLabel(exp, villaById) {
  const scope = exp?.villa_scope;
  if (scope === "project") return "Proyecto general";
  if (scope === "villa") {
    const v = villaById[exp.villa_id];
    return v?.name || exp.villa_id || "Villa";
  }
  if (scope === "multi") {
    const ids = exp.villa_ids || [];
    const names = ids.map((id) => villaById[id]?.name || id);
    return `Varias · ${names.slice(0, 2).join(", ")}${names.length > 2 ? "…" : ""}`;
  }
  return "—";
}

export default function ExpensesPage() {
  const [expenses, setExpenses] = useState([]);
  const [villas, setVillas] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [quarter, setQuarter] = useState(currentQuarter());
  const [filterCategory, setFilterCategory] = useState("");
  const [filterVilla, setFilterVilla] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  // Client-side filters (not sent to backend, applied over the already
  // loaded list). KPIs and the quarterly report modal keep reflecting the
  // server-filtered scope — accepted UX limitation for V1.
  const [filterProvider, setFilterProvider] = useState("");
  const [filterScope, setFilterScope] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);   // full expense doc when editing
  const [attachmentsFor, setAttachmentsFor] = useState(null);   // expense_id being managed
  const [showReport, setShowReport] = useState(false);

  const villaById = useMemo(
    () => Object.fromEntries(villas.map((v) => [v.villa_id, v])),
    [villas]
  );

  const visibleExpenses = useMemo(() => {
    const needle = filterProvider.trim().toLowerCase();
    return expenses.filter((e) => {
      if (needle && !(e.provider || "").toLowerCase().includes(needle)) return false;
      if (filterScope && e.villa_scope !== filterScope) return false;
      return true;
    });
  }, [expenses, filterProvider, filterScope]);

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (quarter) params.set("quarter", quarter);
    if (filterCategory) params.set("category", filterCategory);
    if (filterVilla) params.set("villa_id", filterVilla);
    if (filterStatus) params.set("status", filterStatus);
    if (showArchived) params.set("include_archived", "true");
    try {
      const [listRes, sumRes, villasRes] = await Promise.all([
        api.get(`/expenses?${params.toString()}`),
        api.get(`/expenses/summary?${params.toString()}`),
        villas.length === 0 ? api.get("/villas") : Promise.resolve({ data: villas }),
      ]);
      setExpenses(listRes.data || []);
      setSummary(sumRes.data || null);
      if (villas.length === 0) setVillas(villasRes.data || []);
      setError("");
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quarter, filterCategory, filterVilla, filterStatus, showArchived]);

  const onCreated = async () => {
    setShowForm(false);
    setEditing(null);
    await load();
  };

  const archive = async (exp) => {
    if (!window.confirm(`¿Archivar el gasto "${exp.provider}" (${fmtEur(exp.amount, exp.currency)})? Podrás verlo activando "Mostrar archivados".`)) return;
    try {
      await api.post(`/expenses/${exp.expense_id}/archive`, {});
      await load();
    } catch (e) {
      setError(formatError(e));
    }
  };

  return (
    <AdminShell>
      <div className="mb-5">
        <div className="flex items-center gap-2 text-stone-500 text-xs tracking-widest uppercase">
          <Receipt size={14} /> Admin · Contabilidad
        </div>
        <h2 className="font-serif-dv text-3xl leading-tight mt-1">Gastos internos</h2>
        <p className="text-stone-500 mt-1 text-sm">
          Registro de gastos del proyecto para reportes trimestrales y preparación contable.
        </p>
      </div>

      {error && (
        <div className="mb-4 text-sm px-3 py-2 rounded-md border bg-red-50 border-red-200 text-red-800" data-testid="expenses-error">
          {error}
        </div>
      )}

      {/* Filtros */}
      <div className="dv-card mb-5" data-testid="expenses-filters">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <label className="block">
            <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Trimestre</div>
            <select
              className="dv-input w-full"
              value={quarter}
              onChange={(e) => setQuarter(e.target.value)}
              data-testid="expenses-filter-quarter"
            >
              {buildQuarterOptions().map((q) => (
                <option key={q} value={q}>{q}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Categoría</div>
            <select
              className="dv-input w-full"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              data-testid="expenses-filter-category"
            >
              <option value="">Todas</option>
              {EXPENSE_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Proveedor</div>
            <input
              type="text"
              className="dv-input w-full"
              value={filterProvider}
              onChange={(e) => setFilterProvider(e.target.value)}
              placeholder="Buscar proveedor…"
              data-testid="expenses-filter-provider"
            />
          </label>
          <label className="block">
            <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Estado</div>
            <select
              className="dv-input w-full"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              data-testid="expenses-filter-status"
            >
              <option value="">Todos</option>
              {EXPENSE_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Ámbito</div>
            <select
              className="dv-input w-full"
              value={filterScope}
              onChange={(e) => setFilterScope(e.target.value)}
              data-testid="expenses-filter-scope"
            >
              <option value="">Todos</option>
              <option value="project">Proyecto general</option>
              <option value="villa">Villa específica</option>
              <option value="multi">Varias villas</option>
            </select>
          </label>
          <label className="block">
            <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Villa</div>
            <select
              className="dv-input w-full"
              value={filterVilla}
              onChange={(e) => setFilterVilla(e.target.value)}
              data-testid="expenses-filter-villa"
            >
              <option value="">Todas</option>
              {villas.map((v) => (
                <option key={v.villa_id} value={v.villa_id}>{v.name || v.villa_id}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-stone-100">
          <label className="flex items-center gap-2 text-xs text-stone-600">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              data-testid="expenses-filter-archived"
            />
            Mostrar archivados
          </label>
          <button
            type="button"
            onClick={load}
            className="ml-auto text-xs px-2 py-1.5 rounded border border-stone-300 text-stone-700 hover:bg-stone-50 inline-flex items-center gap-1"
            data-testid="expenses-refresh"
            title="Recargar"
          >
            <ArrowClockwise size={12} />
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5" data-testid="expenses-kpis">
        <StatCard label="Total trimestre" value={fmtEur(summary?.total || 0)} accent={ACCENT} testid="expenses-kpi-total" />
        <StatCard label="Pagado"          value={fmtEur(summary?.paid || 0)}  accent={ACCENT} testid="expenses-kpi-paid" />
        <StatCard label="Pendiente"       value={fmtEur(summary?.pending || 0)} accent={ACCENT} testid="expenses-kpi-pending" />
        <StatCard label="Reembolsado"     value={fmtEur(summary?.reimbursed || 0)} accent={ACCENT} testid="expenses-kpi-reimbursed" />
      </div>

      {summary?.currency_warning && (
        <div className="mb-4 text-xs px-3 py-2 rounded-md border bg-amber-50 border-amber-200 text-amber-900" data-testid="expenses-currency-warning">
          ⚠ {summary.currency_warning} Monedas presentes: {(summary.currencies || []).join(", ")}.
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="text-xs text-stone-500">
          {loading ? "Cargando…" : `${visibleExpenses.length} gasto${visibleExpenses.length === 1 ? "" : "s"} en este filtro`}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            className="text-xs px-3 py-1.5 rounded border border-stone-300 text-stone-700 hover:bg-stone-50 inline-flex items-center gap-1"
            onClick={() => setShowReport(true)}
            data-testid="expenses-view-report"
            disabled={!summary}
          >
            <ChartBar size={14} /> Ver reporte
          </button>
          <button
            type="button"
            className="text-xs px-3 py-1.5 rounded border border-stone-300 text-stone-700 hover:bg-stone-50 inline-flex items-center gap-1"
            onClick={() => exportExpensesCSV(visibleExpenses, villaById, quarter)}
            data-testid="expenses-export-csv"
            disabled={visibleExpenses.length === 0}
          >
            <DownloadSimple size={14} /> Exportar CSV
          </button>
          <button
            type="button"
            className="dv-btn dv-btn-primary text-sm"
            onClick={() => { setEditing(null); setShowForm(true); }}
            data-testid="expenses-create"
          >
            <Plus size={14} /> Nuevo gasto
          </button>
        </div>
      </div>

      {/* Tabla */}
      {visibleExpenses.length === 0 && !loading ? (
        <div className="dv-card text-center py-10 text-stone-500 text-sm" data-testid="expenses-empty">
          No hay gastos en este filtro.
        </div>
      ) : (
        <div className="dv-card p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-stone-200 text-left">
              <tr className="text-xs uppercase tracking-wider text-stone-500">
                <th className="px-4 py-2">Fecha</th>
                <th className="px-4 py-2">Proveedor</th>
                <th className="px-4 py-2">Categoría</th>
                <th className="px-4 py-2">Ámbito</th>
                <th className="px-4 py-2 text-right">Importe</th>
                <th className="px-4 py-2">Estado</th>
                <th className="px-4 py-2 text-center">Adj.</th>
                <th className="px-4 py-2 text-right">Acción</th>
              </tr>
            </thead>
            <tbody>
              {visibleExpenses.map((e) => {
                const isArchived = !!e.archived_at;
                return (
                  <tr
                    key={e.expense_id}
                    className={`border-b border-stone-100 ${isArchived ? "opacity-60" : ""}`}
                    data-testid={`expense-row-${e.expense_id}`}
                  >
                    <td className="px-4 py-2 text-stone-700">{e.date || "—"}</td>
                    <td className="px-4 py-2 font-medium">{e.provider}</td>
                    <td className="px-4 py-2 text-stone-600">{CATEGORY_LABEL[e.category] || e.category}</td>
                    <td className="px-4 py-2 text-stone-600">{villaScopeLabel(e, villaById)}</td>
                    <td className="px-4 py-2 text-right font-medium">{fmtEur(e.amount, e.currency)}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex text-[11px] px-2 py-0.5 rounded-full border ${STATUS_TONE[e.status] || STATUS_TONE.pending}`}>
                        {isArchived ? "Archivado" : (STATUS_LABEL[e.status] || e.status)}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => setAttachmentsFor(e.expense_id)}
                        className="text-xs text-stone-500 hover:text-stone-900 inline-flex items-center gap-1"
                        data-testid={`expense-attachments-${e.expense_id}`}
                        title="Adjuntos"
                      >
                        <Paperclip size={12} />
                      </button>
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => { setEditing(e); setShowForm(true); }}
                        className="text-xs px-2 py-1 rounded border border-stone-300 text-stone-700 hover:bg-stone-100 mr-1"
                        data-testid={`expense-edit-${e.expense_id}`}
                      >Editar</button>
                      {!isArchived && (
                        <button
                          type="button"
                          onClick={() => archive(e)}
                          className="text-xs px-2 py-1 rounded border border-stone-300 text-stone-600 hover:bg-stone-100 inline-flex items-center gap-1"
                          data-testid={`expense-archive-${e.expense_id}`}
                        >
                          <Archive size={12} /> Archivar
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <ExpenseFormModal
          initial={editing}
          villas={villas}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={onCreated}
        />
      )}

      {attachmentsFor && (
        <AttachmentsModal
          expenseId={attachmentsFor}
          onClose={() => setAttachmentsFor(null)}
        />
      )}

      {showReport && (
        <QuarterlyReportModal
          quarter={quarter}
          summary={summary}
          expenses={visibleExpenses}
          villaById={villaById}
          onClose={() => setShowReport(false)}
        />
      )}
    </AdminShell>
  );
}

function ExpenseFormModal({ initial, villas, onClose, onSaved }) {
  const isEdit = !!initial?.expense_id;
  const [form, setForm] = useState(() => ({
    date: initial?.date || new Date().toISOString().slice(0, 10),
    provider: initial?.provider || "",
    category: initial?.category || "other",
    villa_scope: initial?.villa_scope || "project",
    villa_id: initial?.villa_id || "",
    villa_ids: initial?.villa_ids || [],
    amount: initial?.amount != null ? String(initial.amount) : "",
    currency: initial?.currency || "EUR",
    status: initial?.status || "pending",
    note: initial?.note || "",
  }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const toggleVillaMulti = (id) => {
    setForm((f) => {
      const has = f.villa_ids.includes(id);
      return { ...f, villa_ids: has ? f.villa_ids.filter((x) => x !== id) : [...f.villa_ids, id] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const provider = String(form.provider || "").trim();
    const amount = Number(form.amount);
    if (!provider) { setErr("Proveedor obligatorio."); return; }
    if (!Number.isFinite(amount) || amount <= 0) { setErr("Importe debe ser > 0."); return; }
    if (form.villa_scope === "villa" && !form.villa_id) { setErr("Selecciona una villa concreta."); return; }
    if (form.villa_scope === "multi" && (form.villa_ids || []).length < 1) { setErr("Selecciona al menos una villa."); return; }

    setBusy(true);
    try {
      const payload = {
        date: form.date,
        provider,
        category: form.category,
        villa_scope: form.villa_scope,
        villa_id: form.villa_scope === "villa" ? form.villa_id : null,
        villa_ids: form.villa_scope === "multi" ? form.villa_ids : null,
        amount,
        currency: form.currency,
        status: form.status,
        note: form.note || null,
      };
      if (isEdit) {
        await api.patch(`/expenses/${initial.expense_id}`, payload);
      } else {
        await api.post("/expenses", payload);
      }
      await onSaved();
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      data-testid="expense-form-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <form
        onSubmit={submit}
        className="dv-card max-w-xl w-full !p-6 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-serif-dv text-2xl">{isEdit ? "Editar gasto" : "Nuevo gasto"}</h3>
          <button type="button" onClick={onClose} className="text-stone-400 hover:text-stone-800" data-testid="expense-form-close">
            <X size={18} />
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Fecha *</div>
            <input
              type="date"
              className="dv-input w-full"
              value={form.date}
              onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
              required
              data-testid="expense-form-date"
            />
          </label>
          <label className="block">
            <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Proveedor *</div>
            <input
              type="text"
              className="dv-input w-full"
              maxLength={200}
              value={form.provider}
              onChange={(e) => setForm((f) => ({ ...f, provider: e.target.value }))}
              required
              data-testid="expense-form-provider"
            />
          </label>
          <label className="block">
            <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Categoría *</div>
            <select
              className="dv-input w-full"
              value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              data-testid="expense-form-category"
            >
              {EXPENSE_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Estado *</div>
            <select
              className="dv-input w-full"
              value={form.status}
              onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
              data-testid="expense-form-status"
            >
              {EXPENSE_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </label>

          <div className="sm:col-span-2">
            <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Ámbito *</div>
            <div className="flex gap-3 flex-wrap">
              {["project", "villa", "multi"].map((s) => (
                <label key={s} className="inline-flex items-center gap-1.5 text-sm">
                  <input
                    type="radio"
                    name="villa_scope"
                    value={s}
                    checked={form.villa_scope === s}
                    onChange={() => setForm((f) => ({ ...f, villa_scope: s }))}
                    data-testid={`expense-form-scope-${s}`}
                  />
                  {s === "project" ? "Proyecto general" : s === "villa" ? "Villa concreta" : "Varias villas"}
                </label>
              ))}
            </div>
          </div>

          {form.villa_scope === "villa" && (
            <label className="block sm:col-span-2">
              <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Villa *</div>
              <select
                className="dv-input w-full"
                value={form.villa_id}
                onChange={(e) => setForm((f) => ({ ...f, villa_id: e.target.value }))}
                required
                data-testid="expense-form-villa-id"
              >
                <option value="">— Selecciona una villa —</option>
                {villas.map((v) => (
                  <option key={v.villa_id} value={v.villa_id}>{v.name || v.villa_id}</option>
                ))}
              </select>
            </label>
          )}

          {form.villa_scope === "multi" && (
            <div className="sm:col-span-2">
              <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Villas (al menos 1)</div>
              <div className="flex flex-wrap gap-2 border border-stone-200 rounded p-2 bg-stone-50/40 max-h-32 overflow-auto">
                {villas.map((v) => (
                  <label key={v.villa_id} className="inline-flex items-center gap-1.5 text-xs bg-white border border-stone-200 rounded px-2 py-1 cursor-pointer hover:bg-stone-50">
                    <input
                      type="checkbox"
                      checked={form.villa_ids.includes(v.villa_id)}
                      onChange={() => toggleVillaMulti(v.villa_id)}
                      data-testid={`expense-form-villa-multi-${v.villa_id}`}
                    />
                    {v.name || v.villa_id}
                  </label>
                ))}
              </div>
            </div>
          )}

          <label className="block">
            <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Importe *</div>
            <input
              type="number"
              step="0.01"
              min="0"
              className="dv-input w-full"
              value={form.amount}
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
              required
              data-testid="expense-form-amount"
            />
          </label>
          <label className="block">
            <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Moneda *</div>
            <select
              className="dv-input w-full"
              value={form.currency}
              onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
              data-testid="expense-form-currency"
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </label>

          <label className="block sm:col-span-2">
            <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Nota interna</div>
            <textarea
              rows={3}
              className="dv-input w-full"
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              placeholder="Contexto del gasto, referencia, etc."
              data-testid="expense-form-note"
            />
          </label>
        </div>

        {err && (
          <div className="mt-3 text-sm px-3 py-2 rounded-md border bg-red-50 border-red-200 text-red-800" data-testid="expense-form-error">
            {err}
          </div>
        )}

        <div className="mt-4 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy} className="dv-btn dv-btn-ghost text-sm">Cancelar</button>
          <button type="submit" disabled={busy} className="dv-btn dv-btn-primary text-sm" data-testid="expense-form-save">
            {busy ? "Guardando…" : (isEdit ? "Guardar cambios" : "Crear gasto")}
          </button>
        </div>

        {isEdit && (
          <div className="mt-3 text-[11px] text-stone-500">
            Para adjuntar facturas, recibos o comprobantes, guarda primero y usa el icono <Paperclip size={11} className="inline align-middle" /> de la fila.
          </div>
        )}
      </form>
    </div>
  );
}

function AttachmentsModal({ expenseId, onClose }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/expenses/${expenseId}/attachments`);
      setFiles(data || []);
      setErr("");
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [expenseId]);

  const upload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setBusy(true);
    setErr("");
    try {
      for (const file of fileList) {
        const b64 = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(String(r.result).split(",")[1] || "");
          r.onerror = rej;
          r.readAsDataURL(file);
        });
        await api.post(`/expenses/${expenseId}/attachments`, {
          filename: file.name,
          mime: file.type || "application/octet-stream",
          base64: b64,
        });
      }
      await load();
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (fileId) => {
    if (!window.confirm("¿Eliminar este adjunto?")) return;
    try {
      await api.delete(`/expenses/${expenseId}/attachments/${fileId}`);
      await load();
    } catch (e) {
      setErr(formatError(e));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      data-testid="expense-attachments-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="dv-card max-w-md w-full !p-6">
        <div className="flex items-start justify-between mb-3">
          <h3 className="font-serif-dv text-xl">Adjuntos</h3>
          <button type="button" onClick={onClose} className="text-stone-400 hover:text-stone-800">
            <X size={18} />
          </button>
        </div>

        {err && (
          <div className="mb-3 text-sm px-3 py-2 rounded-md border bg-red-50 border-red-200 text-red-800">
            {err}
          </div>
        )}

        <label className="dv-btn dv-btn-outline text-sm w-full justify-center cursor-pointer inline-flex items-center gap-2 mb-3">
          <Paperclip size={14} /> {busy ? "Subiendo…" : "Añadir archivos"}
          <input
            type="file"
            hidden
            multiple
            onChange={(e) => upload(e.target.files)}
            data-testid="expense-attachments-upload"
          />
        </label>

        {loading ? (
          <div className="text-sm text-stone-500 text-center py-4">Cargando…</div>
        ) : files.length === 0 ? (
          <div className="text-sm text-stone-500 text-center py-4">Sin adjuntos todavía.</div>
        ) : (
          <ul className="divide-y divide-stone-100 border border-stone-200 rounded">
            {files.map((f) => (
              <li key={f.file_id} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`expense-attachment-${f.file_id}`}>
                <Paperclip size={12} className="text-stone-400 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="truncate">{f.filename}</div>
                  <div className="text-[10px] text-stone-400">{f.mime} · {Math.round((f.size_bytes || 0) / 1024)} KB</div>
                </div>
                <button
                  type="button"
                  onClick={() => remove(f.file_id)}
                  className="text-stone-400 hover:text-red-600"
                  title="Eliminar"
                >
                  <X size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// QuarterlyReportModal — read-only summary view for the currently loaded
// filter. Reuses the summary payload the page already fetched, so opening
// the modal is free; no extra network call. Aggregations for by_provider /
// by_villa are pre-sorted server-side.
function QuarterlyReportModal({ quarter, summary, expenses, villaById, onClose }) {
  const total = summary?.total || 0;
  const pct = (n) => (total > 0 ? Math.round((Number(n) / total) * 100) : 0);
  const rangeLabel = formatDateRange(summary?.date_range);
  const byCategory = Object.entries(summary?.by_category || {})
    .map(([k, v]) => ({ key: k, total: v }))
    .filter((r) => r.total > 0)
    .sort((a, b) => b.total - a.total);
  const byProvider = summary?.by_provider || [];
  const byVilla = summary?.by_villa || [];

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 py-8 px-4"
      onClick={onClose}
      data-testid="expense-report-modal"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-stone-500 flex items-center gap-1">
              <ChartBar size={12} /> Reporte trimestral de gastos
            </div>
            <h3 className="font-serif-dv text-2xl mt-1">
              {quarter}{rangeLabel ? ` · ${rangeLabel}` : ""}
            </h3>
            <p className="text-xs text-stone-500 mt-1">
              Resumen para revisión contable · {summary?.count || 0} gasto{(summary?.count || 0) === 1 ? "" : "s"} en este filtro.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-stone-400 hover:text-stone-900" title="Cerrar">
            <X size={18} />
          </button>
        </div>

        {/* Totales */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <StatCard label="Total" value={fmtEur(summary?.total || 0)} accent={ACCENT} testid="report-kpi-total" />
          <StatCard label="Pagado" value={fmtEur(summary?.paid || 0)} accent={ACCENT} testid="report-kpi-paid" />
          <StatCard label="Pendiente" value={fmtEur(summary?.pending || 0)} accent={ACCENT} testid="report-kpi-pending" />
          <StatCard label="Reembolsado" value={fmtEur(summary?.reimbursed || 0)} accent={ACCENT} testid="report-kpi-reimbursed" />
        </div>

        {summary?.currency_warning && (
          <div className="mb-4 text-xs px-3 py-2 rounded-md border bg-amber-50 border-amber-200 text-amber-900">
            ⚠ {summary.currency_warning} Monedas presentes: {(summary.currencies || []).join(", ")}.
          </div>
        )}

        {/* Por categoría */}
        <Section title="Por categoría">
          {byCategory.length === 0 ? (
            <div className="text-xs text-stone-500 py-2">Sin datos.</div>
          ) : (
            <table className="w-full text-sm" data-testid="report-by-category">
              <thead className="text-xs uppercase tracking-wider text-stone-500 border-b border-stone-200">
                <tr>
                  <th className="px-3 py-2 text-left">Categoría</th>
                  <th className="px-3 py-2 text-right">Importe</th>
                  <th className="px-3 py-2 text-right w-16">%</th>
                </tr>
              </thead>
              <tbody>
                {byCategory.map((r) => (
                  <tr key={r.key} className="border-b border-stone-100">
                    <td className="px-3 py-1.5 text-stone-700">{CATEGORY_LABEL[r.key] || r.key}</td>
                    <td className="px-3 py-1.5 text-right font-medium">{fmtEur(r.total)}</td>
                    <td className="px-3 py-1.5 text-right text-stone-500">{pct(r.total)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>

        {/* Por proveedor */}
        <Section title={`Por proveedor${byProvider.length >= 50 ? " (top 50)" : ""}`}>
          {byProvider.length === 0 ? (
            <div className="text-xs text-stone-500 py-2">Sin datos.</div>
          ) : (
            <table className="w-full text-sm" data-testid="report-by-provider">
              <thead className="text-xs uppercase tracking-wider text-stone-500 border-b border-stone-200">
                <tr>
                  <th className="px-3 py-2 text-left">Proveedor</th>
                  <th className="px-3 py-2 text-right">Nº gastos</th>
                  <th className="px-3 py-2 text-right">Importe</th>
                  <th className="px-3 py-2 text-right w-16">%</th>
                </tr>
              </thead>
              <tbody>
                {byProvider.map((r) => (
                  <tr key={r.provider} className="border-b border-stone-100">
                    <td className="px-3 py-1.5 text-stone-700">{r.provider}</td>
                    <td className="px-3 py-1.5 text-right text-stone-500">{r.count}</td>
                    <td className="px-3 py-1.5 text-right font-medium">{fmtEur(r.total)}</td>
                    <td className="px-3 py-1.5 text-right text-stone-500">{pct(r.total)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>

        {/* Por villa */}
        <Section title="Por villa / proyecto">
          {byVilla.length === 0 ? (
            <div className="text-xs text-stone-500 py-2">Sin datos.</div>
          ) : (
            <>
              <table className="w-full text-sm" data-testid="report-by-villa">
                <thead className="text-xs uppercase tracking-wider text-stone-500 border-b border-stone-200">
                  <tr>
                    <th className="px-3 py-2 text-left">Villa</th>
                    <th className="px-3 py-2 text-right">Nº gastos</th>
                    <th className="px-3 py-2 text-right">Importe</th>
                  </tr>
                </thead>
                <tbody>
                  {byVilla.map((r) => (
                    <tr key={r.villa_id || "__project__"} className="border-b border-stone-100">
                      <td className="px-3 py-1.5 text-stone-700">
                        {r.villa_id === null
                          ? <span className="italic text-stone-500">Proyecto general</span>
                          : (villaById[r.villa_id]?.name || r.villa_id)}
                      </td>
                      <td className="px-3 py-1.5 text-right text-stone-500">{r.count}</td>
                      <td className="px-3 py-1.5 text-right font-medium">{fmtEur(r.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[11px] text-stone-400 mt-2">
                Nota: los gastos con scope "Varias villas" se contabilizan una vez por cada villa asociada, por lo que la suma de este desglose puede superar el total.
              </p>
            </>
          )}
        </Section>

        {/* Acciones */}
        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            type="button"
            className="text-xs px-3 py-1.5 rounded border border-stone-300 text-stone-700 hover:bg-stone-50 inline-flex items-center gap-1"
            onClick={() => exportExpensesCSV(expenses, villaById, quarter)}
            data-testid="report-export-csv"
            disabled={expenses.length === 0}
          >
            <DownloadSimple size={14} /> Exportar CSV
          </button>
          <button
            type="button"
            className="text-xs px-3 py-1.5 rounded bg-stone-900 text-white hover:bg-stone-800"
            onClick={onClose}
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
